// ---------------------------------------------------------------------------
// Umami analytics — privacy-first, cookieless web analytics.
//
// Loads only when REACT_APP_UMAMI_WEBSITE_ID is set, so local/dev builds stay
// clean and nothing tracks until you drop the id into the deploy env.
//
// Cloud setup: create a website at https://cloud.umami.is, copy its
// "Website ID", and set REACT_APP_UMAMI_WEBSITE_ID at build time.
//   REACT_APP_UMAMI_WEBSITE_ID=xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
//   REACT_APP_UMAMI_SRC=https://cloud.umami.is/script.js   (optional override)
// ---------------------------------------------------------------------------

const WEBSITE_ID = process.env['REACT_APP_UMAMI_WEBSITE_ID'];
const SCRIPT_SRC = process.env['REACT_APP_UMAMI_SRC'] || 'https://cloud.umami.is/script.js';

declare global {
  interface Window {
    umami?: {
      track: (
        event?: string | ((props: Record<string, unknown>) => Record<string, unknown>),
        data?: Record<string, unknown>,
      ) => void;
    };
  }
}

/** Typed event names — single source of truth for everything we track. */
export type AnalyticsEvent =
  // ── Conversion funnel ──
  | 'login-success'
  | 'kyc-complete'
  | 'kyc-currency-select'
  | 'kyc-avatar-select'
  | 'join-submit'
  | 'join-view'
  | 'join-contact-method'
  // ── Engagement ──
  | 'language-switch'
  | 'theme-change'
  | 'globe-open'
  | 'cargo-open'
  | 'investor-panel-switch'
  | 'investor-view-mode'
  | 'investor-logout'
  | 'investor-settings-save'
  | 'investor-password-change'
  | 'investor-avatar-select'
  | 'map-marker-click'
  // ── Landing interactions ──
  | 'landing-section'
  | 'join-click'
  | 'login-cta'
  // ── Shop ──
  | 'shop-open'
  | 'order-submit'
  | 'product-open'
  | 'product-share'
  | 'product-variant-select'
  | 'product-to-form'
  | 'order-contact-method'
  | 'bundle-open'
  | 'bundle-to-form'
  | 'bundle-order-submit'
  | 'bundle-contact-method'
  // ── Gallery ──
  | 'gallery-item-open'
  | 'gallery-scroll'
  // ── Contact ──
  | 'contact-us-click'
  | 'contact-us-submit'
  | 'contact-method-select'
  // ── Navigation ──
  | 'nav-click'
  | 'page-view'
  // ── Journeys ──
  | 'journey_section_viewed'
  | 'journey_card_clicked'
  | 'journey_detail_viewed'
  | 'journey_interest_submitted'
  | 'journey_guide_section_viewed'
  // ── Boutique marketplace ──
  | 'boutique_directory_viewed'
  | 'boutique_card_clicked'
  | 'boutique_page_viewed'
  | 'boutique_product_clicked'
  | 'boutique_journey_clicked'
  | 'boutique_contact_clicked'
  | 'product_size_selected'
  | 'product_option_selected'
  | 'product_custom_order_clicked'
  | 'product_international_badge_viewed'
  | 'product_local_only_notify_clicked'
  | 'product_notify_email_submitted'
  // ── Globe layers ──
  | 'globe_layer_changed'
  | 'globe_layer_all_viewed'
  | 'globe_layer_trade_viewed'
  | 'globe_layer_investments_viewed'
  | 'globe_layer_shop_viewed'
  | 'globe_layer_community_viewed'
  | 'globe_layer_journeys_viewed'
  // ── Auth / registration ──
  | 'register'
  | 'register-click'
  | 'email-verified'
  | 'login-click'
  | 'resend-verification'
  // ── Bundle ──
  | 'bundle_contact_clicked'
  // ── Cart ──
  | 'investor-group-switch'
  | 'boutique_browse_open'
  | 'add_to_cart'
  | 'cart_checkout';

/** Inject the Umami script once, on app boot. No-op if unconfigured. */
export function initAnalytics(): void {
  if (!WEBSITE_ID || typeof document === 'undefined') return;
  if (document.querySelector('script[data-website-id]')) return;

  const script = document.createElement('script');
  script.async = true;
  script.defer = true;
  script.src = SCRIPT_SRC;
  script.setAttribute('data-website-id', WEBSITE_ID);
  document.head.appendChild(script);
}

/**
 * Track a custom event. Safe to call anywhere — never throws, and silently
 * does nothing when analytics is unconfigured or the script hasn't loaded.
 */
export function track(event: AnalyticsEvent, data?: Record<string, unknown>): void {
  try {
    window.umami?.track(event, data);
  } catch {
    /* analytics must never break the app */
  }
}
